<script setup>
import axios from "axios";
import { onMounted, onUnmounted, ref } from "vue";
import { useAjaxForm } from "@/utils/form";
import { useToast } from "@/plugins/toast";
import Parser from "@/components/Secondary/Content/Parser.vue";


const show = ref(false);
const modalOpen = ref(false);
const form = useAjaxForm();
const loadingButton = ref(null);
const toast = useToast();

let intervalId = null;
const props = defineProps({
    banner: {
        type: Object,
        required: true,
    }
})

const submitRsvp = (isAttending = true) => {
  // Fixed: Accept isAttending parameter so "No" button can pass false
  if (props.banner.event.user_event_response?.is_attending === isAttending) {
    removeRsvp();
    return;
  }

  // Set which button is loading ("yes", "no", or "remove")
  loadingButton.value = isAttending ? "yes" : "no";

  form.post(`/api/event/rsvp`, {
    data: {
      event_id: props.banner.event.id,
      is_attending: isAttending,
    },
    onSuccess(response) {
      props.banner.event = response.data.data;
      loadingButton.value = null;
    },
    onError(error) {
      toast.error(
        error.response?.data?.message || "Failed to update RSVP status"
      );
      // Clear loading state after operation completes
      loadingButton.value = null;
    },
  });
};

const removeRsvp = () => {
  // Set remove button as loading
  loadingButton.value = "remove";

  if (!props.banner.event.user_event_response?.id) {
    // If there's no saved RSVP yet, just clear the local state
    props.banner.event.user_event_response = null;

    loadingButton.value = null;
    return;
  }

  const wasAttending = props.banner.event.user_event_response?.is_attending;

  form.delete(`/api/event/rsvp/${props.banner.event.user_event_response?.id}`, {
    onSuccess: (response) => {
      props.banner.event.user_event_response = null;

      if (response.data?.data?.not_attending_count !== undefined) {
        props.banner.event.not_attending_count =
          response.data.data.not_attending_count;
      } else {
        if (wasAttending === false) {
          props.banner.event.not_attending_count--;
        } else if (wasAttending === true) {
          // Fixed: Also update attending count if removing a "Yes" RSVP
          props.banner.event.attending_count--;
        }
      }

      // Clear loading state
      loadingButton.value = null;
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to remove RSVP"
      );
      // Clear loading state
      loadingButton.value = null;
    },
  });
};


// Open banner link
const visit = () => {
    if (!props.banner?.link || props.banner?.has_ended) return;
    if (props.banner.banner_type == "event") {
        modalOpen.value = !modalOpen.value;
        return;
    }
    window.open(props.banner.link, "_blank");
};
</script>

<template>
    <div class="size-full flex flex-col justify-center gap-2 p-3 bg-primary text-primary-foreground rounded-xl">
        <div class="font-medium flex flex-col gap-y-3">
            <span>{{ banner?.title }}</span>
            <span class="text-sm">{{ banner?.full_date }}</span>
        </div>
        <div class="shrink-0 mt-auto" v-if="banner?.show_btn || banner?.banner_type != 'event'">
            <app-button type="button" @click="visit" compact color="primary-foreground" capitalize>
                {{ banner?.btn_text ?? "View" }}
            </app-button>
        </div>
    </div>

    <app-modal-bottom-sheet :open="modalOpen" @update:open="modalOpen = $event">
        <app-layout>
            <app-layout-body class="p-4">
                <div class="flex flex-col gap-4">
                    <div>
                        <h2 class="text-sm font-semibold">Event</h2>
                        <div class="text-lg font-semibold">{{ banner?.title }}</div>
                    </div>
                    <div>
                        <h2 class="text-sm font-semibold">Date</h2>
                        <div>{{ banner?.full_date }}</div>
                    </div>
                    <div v-if="banner.event">
                        <h2 class="text-sm font-semibold">Location</h2>
                        <div>{{ banner.event.location }}</div>
                    </div>
                    <div v-if="banner.event?.description">
                        <h2 class="text-sm font-semibold">About</h2>
                        <app-line-clamp>
                            <Parser :content="banner.event.description"></Parser>
                        </app-line-clamp>
                    </div>
                </div>
            </app-layout-body>
            <app-layout-footer v-if="banner.event">
                <div class="w-full">
                    <div class="text-sm font-semibold">Will you be attending?</div>
                    <div class="flex gap-4 items-end mt-4">
                        <app-button
                            compact
                            v-delayed-click="() => submitRsvp(false)"
                            :disabled="form.processing"
                            icon="cancel"
                            :color="banner.event.user_event_response?.is_attending === false ? 'danger' : 'danger-border'">No</app-button>
                        <app-button
                            compact
                            v-delayed-click="() => submitRsvp(true)"
                            :disabled="form.processing"
                            icon="check"
                            :color="banner.event.user_event_response?.is_attending === true ? 'primary' : 'primary-border'">Yes</app-button>
                    </div>

                    <app-button
                        class="mt-4"
                        color="primary"
                        as="a"
                        :href="banner.link"
                        target="blank"
                        full
                        v-if="banner.btn_text && banner.link" type="button">{{ banner.btn_text }}</app-button>
                </div>
            </app-layout-footer>
        </app-layout>
    </app-modal-bottom-sheet>
</template>
